import { useEffect, useState } from "react";
import { useLocation, useNavigate, Location } from "react-router-dom";

interface ILocationState
{
  noRerender?: boolean;
}

/**
 * Returns a numeric key that bumps on every pathname change, EXCEPT when the
 * navigation was performed with `state: { noRerender: true }`. Use the returned
 * value as the `key` on a child you want to remount on most route changes but
 * preserve across "same conversation" navigations (e.g. the URL update that
 * happens after sending the first chat message).
 *
 * After a noRerender navigation is honored, this hook scrubs the flag from the
 * current history entry by replacing its state with null, so a later browser
 * back/forward to that entry behaves like a normal route change.
 */
export function usePreservedRouteKey(): number {
  const location: Location<ILocationState> = useLocation();
  const navigate = useNavigate();
  // The pathname this version belongs to is tracked in state, not in a ref.
  // Bumping is a render-phase update, and a render can be thrown away before it
  // commits: the pages call the layout's setAssistant() during their own render,
  // which schedules an update on an ancestor and makes React re-render from
  // there, discarding this component's in-progress state. A ref mutation
  // survives that discard, so the "already handled this pathname" bookkeeping
  // would stick while the bump it guarded was lost — the key stayed put and the
  // child never remounted (the New-chat button appeared to do nothing when the
  // session URL had been reached through a noRerender navigation). State is
  // discarded together with the render, so the bump is simply recomputed.
  const [tracked, setTracked] = useState({
    pathname: location.pathname,
    version: 0,
  });

  let version = tracked.version;
  if (tracked.pathname !== location.pathname) {
    version = location.state?.noRerender ? tracked.version : tracked.version + 1;
    setTracked({ pathname: location.pathname, version });
  }

  useEffect(() => {
    if (location.state?.noRerender) {
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
    }
  }, [location, navigate]);

  return version;
}
